import { ChangeEvent, FormEvent, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { assets } from "../lib/assets";
import {
  getChildProfileDetails,
  updateChildProfileDetails,
  uploadPrivateAvatar,
  type ChildGender,
  type SchoolStage
} from "../services/auth/account";
import { supabase } from "../services/supabase/client";
import { useAppStore } from "../store/useAppStore";

const stageOptions: Array<{ value: SchoolStage; label: string }> = [
  { value: "", label: "暂不设置" },
  { value: "kindergarten_small", label: "幼儿园小班" },
  { value: "kindergarten_middle", label: "幼儿园中班" },
  { value: "kindergarten_large", label: "幼儿园大班" },
  { value: "preschool", label: "学前阶段" },
  { value: "primary_1", label: "小学一年级" }
];

function ageFromBirthDate(value: string) {
  if (!value) return null;
  const birth = new Date(`${value}T00:00:00`);
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const monthDelta = today.getMonth() - birth.getMonth();
  if (monthDelta < 0 || (monthDelta === 0 && today.getDate() < birth.getDate())) age -= 1;
  return age >= 0 ? age : null;
}

export function ProfileEditPage() {
  const { activeChildId, childName, avatarUrl, setChildProfile } = useAppStore();
  const [nickname, setNickname] = useState(childName);
  const [gender, setGender] = useState<ChildGender>("unspecified");
  const [birthDate, setBirthDate] = useState("");
  const [schoolStage, setSchoolStage] = useState<SchoolStage>("");
  const [message, setMessage] = useState("这里的信息只用于个性化学习内容与全站称呼。");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const age = useMemo(() => ageFromBirthDate(birthDate), [birthDate]);

  useEffect(() => {
    let alive = true;
    if (!activeChildId) { setLoading(false); return; }
    void getChildProfileDetails(activeChildId).then((profile) => {
      if (!alive) return;
      setNickname(profile.nickname);
      setGender(profile.gender || "unspecified");
      setBirthDate(profile.birth_date || "");
      setSchoolStage(profile.school_stage || "");
    }).catch((error) => {
      if (alive) setMessage(error instanceof Error ? `资料读取失败：${error.message}` : "资料暂时读取失败。");
    }).finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [activeChildId]);

  async function saveProfile(event: FormEvent) {
    event.preventDefault();
    if (!activeChildId) return setMessage("孩子档案尚未准备好，请重新登录。");
    setBusy(true);
    setMessage("正在保存新的成长档案…");
    try {
      const updated = await updateChildProfileDetails(activeChildId, { nickname, gender, birthDate, schoolStage });
      setChildProfile(updated.nickname, avatarUrl);
      localStorage.setItem("lusky:active-profile", JSON.stringify({ id: activeChildId, nickname: updated.nickname, avatarUrl }));
      setMessage("保存好啦！首页和学习内容会用新的昵称与成长阶段。");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "资料保存失败，请再试一次。");
    } finally { setBusy(false); }
  }

  async function uploadAvatar(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (!file || !activeChildId || !supabase) return;
    setBusy(true);
    setMessage("正在把头像放进私有星光相册…");
    try {
      const { data } = await supabase.auth.getUser();
      if (!data.user) throw new Error("登录状态已过期，请重新登录。");
      const uploaded = await uploadPrivateAvatar(data.user.id, activeChildId, file);
      setChildProfile(nickname || childName, uploaded.signedUrl);
      localStorage.setItem("lusky:active-profile", JSON.stringify({ id: activeChildId, nickname: nickname || childName, avatarUrl: uploaded.signedUrl }));
      setMessage("新头像保存好啦！只有当前家庭账号可以查看。");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "头像上传失败，请再试一次。");
    } finally { setBusy(false); event.target.value = ""; }
  }

  return <section className="profile-edit-page page-view">
    <Link className="profile-edit-back" to="/me" aria-label="返回我的页面">‹</Link>
    <header className="profile-edit-heading"><p>我的 · 用户信息</p><h1>编辑小小探索师档案</h1><span>资料由家长管理，可随成长随时更新。</span></header>
    <form className="profile-edit-card" onSubmit={saveProfile}>
      <aside className="profile-edit-avatar-panel">
        <label className="profile-edit-avatar">
          <img src={avatarUrl || assets.hero} alt={`${childName}的头像`} />
          <span>{busy ? "处理中…" : "更换头像"}</span>
          <input type="file" accept="image/png,image/jpeg,image/webp" onChange={uploadAvatar} disabled={busy} />
        </label>
        <h2>{nickname || childName}</h2>
        <p>点击头像选择新图片</p>
        <small>支持 JPG、PNG、WebP，最大 3MB；头像保存在家庭私有空间。</small>
      </aside>
      <div className="profile-edit-fields">
        {loading ? <div className="profile-edit-loading">露米正在整理成长档案…</div> : <>
          <label className="profile-field">昵称 <i>*</i><input value={nickname} onChange={(event) => setNickname(event.target.value)} maxLength={32} placeholder="小名或喜欢的称呼" required /></label>
          <fieldset className="profile-gender"><legend>性别</legend><div>
            {([{ value: "girl", label: "女孩", icon: "🌷" }, { value: "boy", label: "男孩", icon: "🚀" }, { value: "unspecified", label: "暂不设置", icon: "✨" }] as const).map((item) => <button type="button" key={item.value} className={gender === item.value ? "selected" : ""} onClick={() => setGender(item.value)}><b>{item.icon}</b><span>{item.label}</span></button>)}
          </div></fieldset>
          <div className="profile-row">
            <label className="profile-field">出生日期 <small>选填</small><input type="date" value={birthDate} onChange={(event) => setBirthDate(event.target.value)} max={new Date().toISOString().slice(0, 10)} /></label>
            <div className="profile-age"><span>当前年龄</span><strong>{age === null ? "未设置" : `${age} 岁`}</strong><small>根据出生日期自动计算</small></div>
          </div>
          <label className="profile-field">学龄阶段 <small>选填</small><select value={schoolStage} onChange={(event) => setSchoolStage(event.target.value as SchoolStage)}>{stageOptions.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}</select></label>
          <p className="profile-edit-message" role="status">{message}</p>
          <div className="profile-edit-actions"><Link to="/me">取消</Link><button disabled={busy}>{busy ? "正在保存…" : "保存资料　→"}</button></div>
        </>}
      </div>
    </form>
  </section>;
}
